/**
 * OneSignal Push Notification Provider
 * =====================================
 * 
 * Initializes OneSignal for web push notifications.
 * Must be included near the root of the application.
 * PERFORMANCE: OneSignal SDK is loaded dynamically after page load
 */

import { useEffect, useState } from 'react';

// OneSignal App ID - configured in OneSignal dashboard
const ONESIGNAL_APP_ID = '8e04ea19-7ebe-45ef-9ae2-0ed5d4361b15';

let isInitialized = false;
let initializationAttempted = false;
let OneSignal = null; // PERFORMANCE: Loaded dynamically

export const initOneSignal = async () => {
  // Prevent multiple initialization attempts
  if (initializationAttempted) return;
  initializationAttempted = true;
  
  if (typeof window === 'undefined') return;
  
  // Check if we're on a supported domain
  const hostname = window.location.hostname;
  const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';
  const isProduction = hostname === 'toptiersolutionsnyc.com' || hostname === 'www.toptiersolutionsnyc.com';
  
  // Only log in development
  if (isLocalhost || !isProduction) {
    console.log('[OneSignal] Skipping initialization on non-production domain:', hostname);
    console.log('[OneSignal] Push notifications will work on production domain (toptiersolutionsnyc.com)');
    return;
  }
  
  try {
    // PERFORMANCE: Dynamic import - only loads OneSignal SDK when actually needed
    const module = await import('react-onesignal');
    OneSignal = module.default;
    
    await OneSignal.init({
      appId: ONESIGNAL_APP_ID,
      allowLocalhostAsSecureOrigin: true,
      notifyButton: {
        enable: true,
        size: 'medium',
        position: 'bottom-right',
        prenotify: true,
        showCredit: false,
        text: {
          'tip.state.unsubscribed': 'Get notified about exclusive offers!',
          'tip.state.subscribed': 'You\'re subscribed to notifications',
          'tip.state.blocked': 'You\'ve blocked notifications',
          'dialog.main.title': 'Stay Updated',
          'dialog.main.button.subscribe': 'Subscribe',
          'dialog.main.button.unsubscribe': 'Unsubscribe',
        },
        colors: {
          'circle.background': '#00B050',
          'circle.foreground': 'white',
          'badge.background': '#00B050',
          'badge.foreground': 'white',
          'badge.bordercolor': 'white',
          'pulse.color': '#00B050',
          'dialog.button.background.hovering': '#008040',
          'dialog.button.background.active': '#006030',
          'dialog.button.background': '#00B050',
          'dialog.button.foreground': 'white'
        }
      },
      welcomeNotification: {
        title: 'Welcome to TopTier Solutions!',
        message: 'Thanks for subscribing! You\'ll receive updates on financial tips and exclusive offers.',
      },
      promptOptions: {
        slidedown: {
          prompts: [
            {
              type: 'push',
              autoPrompt: false,
              text: {
                actionMessage: 'Stay updated with financial tips and exclusive offers from TopTier Solutions.',
                acceptButton: 'Allow',
                cancelButton: 'Maybe Later',
              },
              delay: {
                pageViews: 2,
                timeDelay: 10
              }
            }
          ]
        }
      }
    });
    
    isInitialized = true;
    console.log('[OneSignal] Initialized successfully');
    
    // Log subscription state changes
    OneSignal.User.PushSubscription.addEventListener('change', (subscription) => {
      console.log('[OneSignal] Subscription changed:', subscription);
    });
    
  } catch (error) {
    // Silently handle initialization errors in non-production
    if (isProduction) {
      console.error('[OneSignal] Initialization error:', error);
    }
  }
};

/**
 * Hook to use OneSignal in components
 */
export const useOneSignal = () => {
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [permission, setPermission] = useState('default');
  
  useEffect(() => {
    if (typeof window === 'undefined' || !OneSignal) return;
    
    const checkSubscription = async () => {
      try {
        if (OneSignal?.User?.PushSubscription) {
          const optedIn = await OneSignal.User.PushSubscription.optedIn;
          setIsSubscribed(optedIn || false);
        }
        
        if ('Notification' in window) {
          setPermission(Notification.permission);
        }
      } catch (error) {
        console.error('[OneSignal] Error checking subscription:', error);
      }
    };
    
    checkSubscription();
    
    // Listen for changes
    const handleChange = () => checkSubscription();
    OneSignal?.User?.PushSubscription?.addEventListener('change', handleChange);
    
    return () => {
      OneSignal?.User?.PushSubscription?.removeEventListener('change', handleChange);
    };
  }, []);
  
  const promptForSubscription = async () => {
    if (!OneSignal) return;
    try {
      await OneSignal.Slidedown.promptPush();
    } catch (error) {
      console.error('[OneSignal] Error prompting subscription:', error);
    }
  };
  
  const setExternalUserId = async (userId) => {
    if (!OneSignal) return;
    try {
      await OneSignal.login(userId);
      console.log('[OneSignal] External user ID set:', userId);
    } catch (error) {
      console.error('[OneSignal] Error setting external user ID:', error);
    }
  };
  
  const logout = async () => {
    if (!OneSignal) return;
    try {
      await OneSignal.logout();
      console.log('[OneSignal] User logged out');
    } catch (error) {
      console.error('[OneSignal] Error logging out:', error);
    }
  };
  
  const addTag = async (key, value) => {
    if (!OneSignal) return;
    try {
      await OneSignal.User.addTag(key, value);
    } catch (error) {
      console.error('[OneSignal] Error adding tag:', error);
    }
  };
  
  return {
    isSubscribed,
    permission,
    promptForSubscription,
    setExternalUserId,
    logout,
    addTag,
    OneSignal
  };
};

/**
 * Component that initializes OneSignal on mount
 * PERFORMANCE: Defer initialization to after initial render
 */
export const OneSignalProvider = ({ children }) => {
  useEffect(() => {
    // PERFORMANCE: Delay OneSignal initialization to avoid blocking initial load
    // Wait for user interaction or idle time
    const timeoutId = setTimeout(() => {
      if ('requestIdleCallback' in window) {
        requestIdleCallback(() => initOneSignal());
      } else {
        initOneSignal();
      }
    }, 3000); // Wait 3 seconds before loading
    
    return () => clearTimeout(timeoutId);
  }, []);
  
  return children;
};

export default OneSignalProvider;
